import { useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Stack,
  Select,
  Switch,
  Button,
  Group,
  Skeleton,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Badge,
  SimpleGrid,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconBell,
  IconMail,
  IconMessage,
  IconPhone,
  IconDeviceMobile,
  IconShoppingCart,
  IconTruck,
  IconTag,
  IconAlertTriangle,
  IconRefresh,
  IconReceipt,
  IconSparkles,
  IconArrowUp,
  IconArrowDown,
  IconSettings,
  IconPackage,
  IconForklift,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import {
  useCustomerPreferences,
  useUpdatePreferences,
  useInvoiceNotificationPrefs,
  useUpdateInvoiceNotificationPrefs,
  useAlertPreferences,
  useUpdateAlertPreferences,
} from '@/hooks/usePortalApi';

// ─── Constants ───────────────────────────────────────────────────────────────

const DELIVERY_DAYS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
];

const PAYMENT_METHODS = [
  { value: 'invoice', label: 'Invoice (Net Terms)' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'ach', label: 'ACH / Bank Transfer' },
  { value: 'check', label: 'Check' },
];

interface ChannelInfo {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  description: string;
  placeholder?: string;
  fieldType?: 'phone' | 'email';
}

const CHANNELS: ChannelInfo[] = [
  { id: 'sms', label: 'Text / SMS', icon: <IconMessage size={18} />, color: 'green', description: 'Quick text alerts to your phone', placeholder: '(555) 123-4567', fieldType: 'phone' },
  { id: 'voice', label: 'Phone Call', icon: <IconPhone size={18} />, color: 'blue', description: 'Automated voice calls for urgent items', placeholder: '(555) 123-4567', fieldType: 'phone' },
  { id: 'email', label: 'Email', icon: <IconMail size={18} />, color: 'brand', description: 'Detailed notifications with full info', placeholder: 'you@company.com', fieldType: 'email' },
  { id: 'push', label: 'App / Push', icon: <IconDeviceMobile size={18} />, color: 'violet', description: 'Instant push notifications on your device' },
];

interface AlertCategory {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  aiCapable?: boolean;
}

const ALERT_CATEGORIES: AlertCategory[] = [
  { id: 'out_of_stock', label: 'Out of Stock', description: 'An item on your order is unavailable when we go to ship', icon: <IconAlertTriangle size={18} />, color: 'red', aiCapable: true },
  { id: 'low_inventory', label: 'Low Inventory', description: 'Items you buy frequently are running low — order before they sell out', icon: <IconPackage size={18} />, color: 'orange', aiCapable: true },
  { id: 'substitution', label: 'Substitutions', description: 'We substituted an item on your order — approve or reject', icon: <IconRefresh size={18} />, color: 'amber', aiCapable: true },
  { id: 'price_drop', label: 'Price Drops', description: 'Price decreased on products you order regularly', icon: <IconTag size={18} />, color: 'produce' },
  { id: 'order_status', label: 'Order Updates', description: 'Confirmed, packed, shipped, out for delivery, delivered', icon: <IconShoppingCart size={18} />, color: 'brand' },
  { id: 'delivery', label: 'Delivery Alerts', description: 'Driver en route, delivery window, proof of delivery', icon: <IconTruck size={18} />, color: 'cyan' },
  { id: 'invoice', label: 'Invoices & Payments', description: 'New invoices, payment confirmations, overdue reminders', icon: <IconReceipt size={18} />, color: 'slate' },
  { id: 'promos', label: 'Deals & Promotions', description: 'Exclusive specials, seasonal offers, volume discounts', icon: <IconTag size={18} />, color: 'amber' },
];

// ─── Main Component ──────────────────────────────────────────────────────────

export default function Preferences() {
  const { data: prefs, isLoading } = useCustomerPreferences();
  const updatePrefs = useUpdatePreferences();

  // Alert preferences API
  const { data: alertPrefs, isLoading: alertLoading } = useAlertPreferences();
  const updateAlertPrefs = useUpdateAlertPreferences();

  // Channel priority (ranked list)
  const [channels, setChannels] = useState<string[]>(['sms', 'voice', 'email', 'push']);
  const [channelEnabled, setChannelEnabled] = useState<Record<string, boolean>>({
    sms: true, voice: true, email: true, push: false,
  });
  const [channelValues, setChannelValues] = useState<Record<string, string>>({
    sms: '', voice: '', email: '', push: '',
  });

  // Alert toggles
  const [alerts, setAlerts] = useState<Record<string, boolean>>({
    out_of_stock: true,
    low_inventory: true,
    substitution: true,
    price_drop: true,
    order_status: true,
    delivery: true,
    invoice: true,
    promos: true,
  });

  // AI outreach
  const [aiOutreach, setAiOutreach] = useState(true);
  const [aiAutoResolve, setAiAutoResolve] = useState(false);

  // Hydrate local state from API on load
  useEffect(() => {
    if (alertPrefs) {
      if (alertPrefs.channel_priority) setChannels(alertPrefs.channel_priority);
      if (alertPrefs.channels) {
        setChannelEnabled({
          sms: alertPrefs.channels.sms?.enabled ?? true,
          voice: alertPrefs.channels.voice?.enabled ?? true,
          email: alertPrefs.channels.email?.enabled ?? true,
          push: alertPrefs.channels.push?.enabled ?? false,
        });
        setChannelValues({
          sms: alertPrefs.channels.sms?.value ?? '',
          voice: alertPrefs.channels.voice?.value ?? '',
          email: alertPrefs.channels.email?.value ?? '',
          push: '',
        });
      }
      if (alertPrefs.alerts) setAlerts(alertPrefs.alerts);
      setAiOutreach(alertPrefs.ai_outreach_enabled ?? true);
      setAiAutoResolve(alertPrefs.ai_auto_substitute ?? false);
    }
  }, [alertPrefs]);

  // Ordering prefs form
  const form = useForm({
    initialValues: {
      preferred_delivery_day: '' as string | null,
      order_reminders: true,
      promo_emails: true,
      default_payment_method: '' as string | null,
      delivery_window: '' as string | null,
      dock_instructions: '',
      temperature_requirements: '' as string | null,
      default_po_prefix: '',
      standing_order_notes: '',
      substitution_policy: '' as string | null,
    },
  });

  useEffect(() => {
    if (prefs) {
      form.setValues({
        preferred_delivery_day: prefs.preferred_delivery_day || '',
        order_reminders: prefs.order_reminders ?? true,
        promo_emails: prefs.promo_emails ?? true,
        default_payment_method: prefs.default_payment_method || '',
        delivery_window: prefs.delivery_window || '',
        dock_instructions: prefs.dock_instructions || '',
        temperature_requirements: prefs.temperature_requirements || '',
        default_po_prefix: prefs.default_po_prefix || '',
        standing_order_notes: prefs.standing_order_notes || '',
        substitution_policy: prefs.substitution_policy || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs]);

  const handleSubmit = (values: typeof form.values) => {
    updatePrefs.mutate(
      {
        preferred_delivery_day: values.preferred_delivery_day || null,
        order_reminders: values.order_reminders,
        promo_emails: values.promo_emails,
        default_payment_method: values.default_payment_method || null,
        delivery_window: values.delivery_window || null,
        dock_instructions: values.dock_instructions || null,
        temperature_requirements: values.temperature_requirements || null,
        default_po_prefix: values.default_po_prefix || null,
        standing_order_notes: values.standing_order_notes || null,
        substitution_policy: values.substitution_policy || null,
      },
      {
        onSuccess: () => notifications.show({ title: 'Saved', message: 'Preferences updated', color: 'brand' }),
        onError: () => notifications.show({ title: 'Error', message: 'Failed to save', color: 'red' }),
      },
    );
  };

  // Persist helper — saves to API and shows feedback
  const saveAlertPref = useCallback((data: Record<string, any>, label?: string) => {
    updateAlertPrefs.mutate(data, {
      onSuccess: () => {
        if (label) notifications.show({ title: 'Saved', message: label, color: 'brand' });
      },
      onError: () => notifications.show({ title: 'Error', message: 'Failed to save', color: 'red' }),
    });
  }, [updateAlertPrefs]);

  const moveChannel = useCallback((id: string, direction: 'up' | 'down') => {
    setChannels((prev) => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      // Persist new order
      saveAlertPref({ channel_priority: arr });
      return arr;
    });
  }, [saveAlertPref]);

  const toggleAlert = useCallback((id: string) => {
    setAlerts((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveAlertPref(
        { [`alert_${id}`]: next[id] },
        next[id] ? `${ALERT_CATEGORIES.find((c) => c.id === id)?.label} enabled` : `${ALERT_CATEGORIES.find((c) => c.id === id)?.label} disabled`,
      );
      return next;
    });
  }, [saveAlertPref]);

  if (isLoading || alertLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={30} width={200} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  return (
    <Stack gap="xl">
      <PageHeader
        title="Preferences & Notifications"
        description="Control how we reach you and what you get notified about"
      />

      {/* ── How Should We Contact You? ────────────────────────────── */}
      <Paper p="lg" radius="md">
        <Group gap="sm" mb="md">
          <ThemeIcon variant="light" size="lg" radius="xl" color="brand">
            <IconBell size={20} />
          </ThemeIcon>
          <div>
            <Text fw={700} size="lg">How Should We Contact You?</Text>
            <Text size="sm" c="dimmed">
              Drag to rank your preferred contact methods. We'll try your #1 first, then fall back.
            </Text>
          </div>
        </Group>

        <Stack gap="xs">
          {channels.map((chId, idx) => {
            const ch = CHANNELS.find((c) => c.id === chId)!;
            return (
              <Paper
                key={chId}
                withBorder
                p="sm"
                radius="md"
                style={{
                  opacity: channelEnabled[chId] ? 1 : 0.5,
                  borderColor: channelEnabled[chId] ? `var(--mantine-color-${ch.color}-3)` : undefined,
                  background: channelEnabled[chId] && idx === 0 ? `var(--mantine-color-${ch.color}-0)` : undefined,
                }}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="md" wrap="nowrap" style={{ flex: 1 }}>
                    <Group gap={2}>
                      <Tooltip label="Move up">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          disabled={idx === 0}
                          onClick={() => moveChannel(chId, 'up')}
                          aria-label="Move up"
                        >
                          <IconArrowUp size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Move down">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          disabled={idx === channels.length - 1}
                          onClick={() => moveChannel(chId, 'down')}
                          aria-label="Move down"
                        >
                          <IconArrowDown size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                    <Badge variant="filled" color={ch.color} size="lg" circle>
                      {idx + 1}
                    </Badge>
                    <ThemeIcon variant="light" size="md" radius="xl" color={ch.color}>
                      {ch.icon}
                    </ThemeIcon>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} size="sm">{ch.label}</Text>
                      <Text size="xs" c="dimmed">{ch.description}</Text>
                    </div>
                  </Group>
                  <Group gap="sm" wrap="nowrap">
                    {ch.fieldType && channelEnabled[chId] && (
                      <TextInput
                        size="xs"
                        placeholder={ch.placeholder}
                        w={160}
                        value={channelValues[chId] || ''}
                        onChange={(e) =>
                          setChannelValues((prev) => ({ ...prev, [chId]: e.currentTarget.value }))
                        }
                        onBlur={(e) => {
                          const contactField = chId === 'email' ? 'notification_email'
                            : chId === 'sms' ? 'sms_number' : 'voice_number';
                          saveAlertPref({ [contactField]: e.currentTarget.value || null });
                        }}
                      />
                    )}
                    <Switch
                      checked={channelEnabled[chId]}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        setChannelEnabled((prev) => ({ ...prev, [chId]: checked }));
                        saveAlertPref({ [`${chId}_enabled`]: checked });
                      }}
                      aria-label={`Enable ${ch.label}`}
                    />
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      </Paper>

      {/* ── What Should We Notify You About? ──────────────────────── */}
      <Paper p="lg" radius="md">
        <Group gap="sm" mb="md">
          <ThemeIcon variant="light" size="lg" radius="xl" color="produce">
            <IconSettings size={20} />
          </ThemeIcon>
          <div>
            <Text fw={700} size="lg">What Should We Notify You About?</Text>
            <Text size="sm" c="dimmed">
              Toggle on the alerts that matter to you. We handle the rest.
            </Text>
          </div>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {ALERT_CATEGORIES.map((cat) => (
            <Paper
              key={cat.id}
              withBorder
              p="md"
              radius="md"
              style={{
                borderColor: alerts[cat.id] ? `var(--mantine-color-${cat.color}-3)` : undefined,
                background: alerts[cat.id] ? `var(--mantine-color-${cat.color}-0)` : undefined,
                cursor: 'pointer',
                transition: 'all 150ms ease',
              }}
              onClick={() => toggleAlert(cat.id)}
            >
              <Group justify="space-between" wrap="nowrap" align="start">
                <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                  <ThemeIcon variant="light" size="lg" radius="xl" color={cat.color}>
                    {cat.icon}
                  </ThemeIcon>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6}>
                      <Text fw={600} size="sm">{cat.label}</Text>
                      {cat.aiCapable && (
                        <Badge variant="light" size="xs" color="violet" leftSection={<IconSparkles size={10} />}>
                          AI
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed" lineClamp={2}>{cat.description}</Text>
                  </div>
                </Group>
                <Switch
                  checked={alerts[cat.id]}
                  onChange={() => {}}
                  aria-label={`Toggle ${cat.label}`}
                  onClick={(e) => e.stopPropagation()}
                />
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      </Paper>

      {/* ── AI Outreach ───────────────────────────────────────────── */}
      <Paper
        p="lg"
        radius="md"
        style={{
          background: 'linear-gradient(135deg, var(--mantine-color-violet-0) 0%, var(--mantine-color-brand-0) 100%)',
          border: '1px solid var(--mantine-color-violet-2)',
        }}
      >
        <Group gap="sm" mb="md">
          <ThemeIcon variant="filled" size="lg" radius="xl" color="violet">
            <IconSparkles size={20} />
          </ThemeIcon>
          <div>
            <Group gap={6}>
              <Text fw={700} size="lg">AI-Assisted Outreach</Text>
              <Badge variant="filled" color="violet" size="sm">Smart</Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Our AI assistant proactively contacts you about issues before they become problems
            </Text>
          </div>
        </Group>

        <Stack gap="md">
          <Paper withBorder p="md" radius="md" bg="white">
            <Group justify="space-between" wrap="nowrap">
              <div style={{ flex: 1 }}>
                <Text fw={600} size="sm">Proactive out-of-stock outreach</Text>
                <Text size="xs" c="dimmed">
                  If an item on your order goes out of stock before shipment, our AI will contact you
                  using your preferred channel to offer substitutes or let you adjust the order — no
                  surprises at delivery.
                </Text>
              </div>
              <Switch
                checked={aiOutreach}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  setAiOutreach(checked);
                  saveAlertPref({ ai_outreach_enabled: checked }, checked ? 'AI outreach enabled' : 'AI outreach disabled');
                }}
                size="md"
              />
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md" bg="white">
            <Group justify="space-between" wrap="nowrap">
              <div style={{ flex: 1 }}>
                <Text fw={600} size="sm">Auto-approve AI substitutions</Text>
                <Text size="xs" c="dimmed">
                  Let our AI automatically pick the best available substitute when items are out of
                  stock, based on your substitution preferences. You'll still be notified of every swap.
                </Text>
              </div>
              <Switch
                checked={aiAutoResolve}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  setAiAutoResolve(checked);
                  saveAlertPref({ ai_auto_substitute: checked }, checked ? 'Auto-substitution enabled' : 'Auto-substitution disabled');
                }}
                size="md"
              />
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md" bg="white">
            <Group justify="space-between" wrap="nowrap">
              <div style={{ flex: 1 }}>
                <Text fw={600} size="sm">Smart reorder reminders</Text>
                <Text size="xs" c="dimmed">
                  Based on your order history, we'll remind you when it looks like you're running low —
                  with a pre-built cart ready to go.
                </Text>
              </div>
              <Switch
                checked={form.values.order_reminders}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  form.setFieldValue('order_reminders', checked);
                  updatePrefs.mutate({ order_reminders: checked });
                  saveAlertPref({ ai_reorder_reminders: checked });
                }}
                size="md"
              />
            </Group>
          </Paper>
        </Stack>
      </Paper>

      {/* ── Ordering Preferences ──────────────────────────────────── */}
      <Paper p="lg" radius="md">
        <Group gap="sm" mb="md">
          <ThemeIcon variant="light" size="lg" radius="xl" color="brand">
            <IconShoppingCart size={20} />
          </ThemeIcon>
          <Text fw={700} size="lg">Ordering Preferences</Text>
        </Group>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <SimpleGrid cols={{ base: 1, sm: 2 }} maw={600}>
            <Select
              label="Preferred Delivery Day"
              placeholder="Select day"
              data={DELIVERY_DAYS}
              {...form.getInputProps('preferred_delivery_day')}
              clearable
            />
            <Select
              label="Default Payment Method"
              placeholder="Select method"
              data={PAYMENT_METHODS}
              {...form.getInputProps('default_payment_method')}
              clearable
            />
          </SimpleGrid>
          <Group justify="flex-end" mt="md">
            <Button type="submit" loading={updatePrefs.isPending}>
              Save Preferences
            </Button>
          </Group>
        </form>
      </Paper>

      {/* ── Delivery & Fulfillment ────────────────────────────────── */}
      <Paper p="lg" radius="md">
        <Group gap="sm" mb="md">
          <ThemeIcon variant="light" size="lg" radius="xl" color="cyan">
            <IconForklift size={20} />
          </ThemeIcon>
          <div>
            <Text fw={700} size="lg">Delivery & Fulfillment</Text>
            <Text size="sm" c="dimmed">
              Help us handle your orders and deliveries the way you need them
            </Text>
          </div>
        </Group>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md" maw={600}>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Select
                label="Preferred Delivery Window"
                description="When should we deliver?"
                placeholder="No preference"
                data={[
                  { value: 'early_morning', label: 'Early Morning (5-7 AM)' },
                  { value: 'morning', label: 'Morning (7-10 AM)' },
                  { value: 'midday', label: 'Midday (10 AM-1 PM)' },
                  { value: 'afternoon', label: 'Afternoon (1-4 PM)' },
                ]}
                {...form.getInputProps('delivery_window')}
                clearable
              />
              <Select
                label="Substitution Policy"
                description="What to do if an item is out of stock"
                placeholder="Select policy"
                data={[
                  { value: 'allow', label: 'Allow Substitutions' },
                  { value: 'ask_first', label: 'Ask Me First' },
                  { value: 'deny', label: 'Never Substitute' },
                ]}
                {...form.getInputProps('substitution_policy')}
                clearable
              />
              <Select
                label="Temperature Requirements"
                description="Cold chain sensitivity for your deliveries"
                placeholder="Standard"
                data={[
                  { value: 'standard', label: 'Standard' },
                  { value: 'strict_cold', label: 'Strict Cold (reject if >38°F)' },
                  { value: 'frozen', label: 'Frozen Only (reject if >0°F)' },
                  { value: 'ambient', label: 'Ambient OK' },
                ]}
                {...form.getInputProps('temperature_requirements')}
                clearable
              />
              <TextInput
                label="Default PO Prefix"
                description="Auto-populate your PO numbers"
                placeholder="e.g. ACME-"
                {...form.getInputProps('default_po_prefix')}
              />
            </SimpleGrid>
            <Textarea
              label="Loading Dock Instructions"
              description="Dock number, gate code, forklift availability, etc."
              placeholder="e.g. Use Dock #3, gate code 4455, forklift available on-site"
              minRows={2}
              {...form.getInputProps('dock_instructions')}
            />
            <Textarea
              label="Standing Order Notes"
              description="Permanent notes for the delivery driver and warehouse team"
              placeholder="e.g. Always leave with receiving manager, ring bell on arrival"
              minRows={2}
              {...form.getInputProps('standing_order_notes')}
            />
            <Group justify="flex-end">
              <Button type="submit" loading={updatePrefs.isPending}>
                Save Preferences
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>

      {/* ── Invoice & Billing Notifications ───────────────────────── */}
      <Paper p="lg" radius="md">
        <Group gap="sm" mb="md">
          <ThemeIcon variant="light" size="lg" radius="xl" color="slate">
            <IconReceipt size={20} />
          </ThemeIcon>
          <Text fw={700} size="lg">Invoice & Billing Notifications</Text>
        </Group>
        <InvoiceNotificationSection />
      </Paper>
    </Stack>
  );
}

// ─── Invoice Notification Sub-section ────────────────────────────────────────

function InvoiceNotificationSection() {
  const { data: prefs, isLoading } = useInvoiceNotificationPrefs();
  const updatePrefs = useUpdateInvoiceNotificationPrefs();

  if (isLoading) return <Skeleton height={200} />;

  const handleToggle = (field: string, value: boolean) => {
    updatePrefs.mutate(
      { [field]: value },
      {
        onSuccess: () => notifications.show({ title: 'Saved', message: 'Updated', color: 'brand' }),
        onError: () => notifications.show({ title: 'Error', message: 'Failed to save', color: 'red' }),
      },
    );
  };

  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Switch
          label="Invoice emails"
          description="Invoice copies when created"
          checked={prefs?.invoice_email_enabled ?? true}
          onChange={(e) => handleToggle('invoice_email_enabled', e.currentTarget.checked)}
        />
        <Switch
          label="Monthly statements"
          description="Account statements each month"
          checked={prefs?.statement_email_enabled ?? true}
          onChange={(e) => handleToggle('statement_email_enabled', e.currentTarget.checked)}
        />
        <Switch
          label="Payment receipts"
          description="Confirmation when payments process"
          checked={prefs?.payment_receipt_email ?? true}
          onChange={(e) => handleToggle('payment_receipt_email', e.currentTarget.checked)}
        />
        <Switch
          label="Overdue reminders"
          description="Heads-up on past-due invoices"
          checked={prefs?.overdue_reminder_enabled ?? true}
          onChange={(e) => handleToggle('overdue_reminder_enabled', e.currentTarget.checked)}
        />
        <Switch
          label="Early-pay discounts"
          description="Reminder when a discount is available"
          checked={prefs?.early_discount_reminder_enabled ?? true}
          onChange={(e) => handleToggle('early_discount_reminder_enabled', e.currentTarget.checked)}
        />
      </SimpleGrid>
      <TextInput
        label="Invoice notification email"
        description="Override the default email for billing notifications"
        placeholder="Leave blank to use account email"
        maw={400}
        defaultValue={prefs?.preferred_email ?? ''}
        onBlur={(e) => {
          updatePrefs.mutate(
            { preferred_email: e.currentTarget.value || null },
            {
              onSuccess: () => notifications.show({ title: 'Saved', message: 'Email updated', color: 'brand' }),
              onError: () => notifications.show({ title: 'Error', message: 'Failed to save', color: 'red' }),
            },
          );
        }}
      />
    </Stack>
  );
}
